import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Menu, MenuItem } from '@mui/material';
import clsx from 'clsx';
import Tooltip from 'components/Tooltip';
import { IS_SANDBOX_ENV } from 'helper/constant';
import SetupGuideModal from 'layouts/V2/Dashboard/components/SetupGuideModal';
import PropTypes from 'prop-types';

import { selectIsSetupGuideActive } from 'redux/selectors/general';

import styles from '../sidebar.module.css';

import { ReactComponent as CursorIcon } from 'asset/IconsPack/at_cursor-click-01.svg';
import { ReactComponent as FileIcon } from 'asset/IconsPack/at_file-06.svg';
import { ReactComponent as HelpCircleIcon } from 'asset/IconsPack/at_help-circle.svg';
import { ReactComponent as SettingsIcon } from 'asset/IconsPack/at_settings-01.svg';
import { ReactComponent as TicketIcon } from 'asset/IconsPack/at_ticket-01.svg';
import { ReactComponent as ChatIcon } from 'asset/icons/chat_icon.svg';

const LABEL = 'Help & resources';

function HelpMenu({ collapsed, handleHelpClick, onReplayTour }) {
    const isSetupGuideActive = useSelector(selectIsSetupGuideActive);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openSetupGuideModal, setOpenSetupGuideModal] = useState(false);
    const isOpen = Boolean(anchorEl);

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const handleSetupGuide = () => {
        closeMenu();
        setOpenSetupGuideModal(true);
    };

    const handleChatSupport = () => {
        closeMenu();
        handleHelpClick();
    };

    const handleReplayTour = () => {
        closeMenu();
        onReplayTour();
    };

    return (
        <>
            <Tooltip
                title={collapsed && !isOpen ? LABEL : ''}
                placement="right"
                classes={{ tooltip: styles.collapseTooltip }}
            >
                <button
                    type="button"
                    data-tour="help"
                    className={clsx(
                        styles.navItem,
                        styles.navItemButton,
                        collapsed && styles.navItemCollapsed,
                        isOpen && styles.navItemActive
                    )}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    aria-label={collapsed ? LABEL : undefined}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? 'help-menu' : undefined}
                >
                    <span className={styles.navIcon}>
                        <HelpCircleIcon />
                    </span>
                    {!collapsed && <span className={styles.navLabel}>{LABEL}</span>}
                </button>
            </Tooltip>
            <Menu
                id="help-menu"
                open={isOpen}
                anchorEl={anchorEl}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                classes={{ paper: styles.flyoutPaper, list: styles.footerMenuList }}
            >
                <MenuItem
                    component="a"
                    href={
                        IS_SANDBOX_ENV
                            ? 'https://info.authenticate.com/sandbox'
                            : 'https://docs.authenticate.com/'
                    }
                    target="_blank"
                    rel="noreferrer"
                    className={styles.footerMenuItem}
                    onClick={closeMenu}
                >
                    <FileIcon /> Technical Docs
                </MenuItem>
                {!IS_SANDBOX_ENV && !isSetupGuideActive && (
                    <MenuItem className={styles.footerMenuItem} onClick={handleSetupGuide}>
                        <SettingsIcon /> Setup Guide
                    </MenuItem>
                )}
                {onReplayTour && (
                    <MenuItem className={styles.footerMenuItem} onClick={handleReplayTour}>
                        <CursorIcon /> Take the sidebar tour
                    </MenuItem>
                )}
                <MenuItem className={styles.footerMenuItem} onClick={handleChatSupport}>
                    <ChatIcon /> Chat Support
                </MenuItem>
                <MenuItem
                    component="a"
                    href="https://info.authenticate.com/kb-tickets/new"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.footerMenuItem}
                    onClick={closeMenu}
                >
                    <TicketIcon /> Raise a Support Ticket
                </MenuItem>
            </Menu>
            <SetupGuideModal
                open={openSetupGuideModal}
                onClose={() => setOpenSetupGuideModal(false)}
            />
        </>
    );
}

HelpMenu.propTypes = {
    collapsed: PropTypes.bool,
    handleHelpClick: PropTypes.func,
    onReplayTour: PropTypes.func,
};

export default HelpMenu;
